import { useEffect, useState } from "react";
import { ExternalLink, Link2, Loader2, Plus, Unlink } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import {
  useCreateTicket,
  useLinkTicket,
  useRequestTickets,
  useUnlinkTicket,
} from "@/hooks/useRequests";
import type { BlinkRequest } from "@/lib/types";

interface JiraTicketsCardProps {
  request: BlinkRequest;
}

/**
 * Jira tickets for a request: raise a new one (defects: BDS, EBDS or ND) or
 * link any number of existing tickets by key or URL.
 */
export function JiraTicketsCard({ request }: JiraTicketsCardProps) {
  const { toast } = useToast();
  const { data, isLoading } = useRequestTickets(request.id);
  const { mutate: create, isPending } = useCreateTicket(request.id);
  const { mutate: link, isPending: isLinking } = useLinkTicket(request.id);
  const { mutate: unlink, isPending: isUnlinking } = useUnlinkTicket(
    request.id,
  );
  const [toUnlink, setToUnlink] = useState<string | null>(null);
  const [linkInput, setLinkInput] = useState("");
  const [open, setOpen] = useState(false);
  const [project, setProject] = useState("");
  const [summary, setSummary] = useState(request.title);
  const [copyAttachments, setCopyAttachments] = useState(true);

  useEffect(() => {
    if (open) {
      setProject("");
      setSummary(request.title);
      setCopyAttachments(true);
    }
  }, [open, request.title]);

  const tickets = data?.tickets ?? [];
  const projects = data?.projects ?? [];

  const handleCreate = () => {
    if (!project || !summary.trim()) return;
    create(
      {
        project_key: project,
        summary: summary.trim(),
        copy_attachments: copyAttachments,
      },
      {
        onSuccess: (ticket) => {
          toast({
            title: `${ticket.issue_key} created`,
            description: "Linked to this request in Jira.",
          });
          setOpen(false);
        },
        onError: (err) =>
          toast({
            title: "Could not create the ticket",
            description: err.message,
            variant: "destructive",
          }),
      },
    );
  };

  const handleUnlink = () => {
    if (!toUnlink) return;
    const key = toUnlink;
    unlink(key, {
      onSuccess: () => {
        toast({
          title: `${key} unlinked`,
          description: "The Jira ticket itself wasn't changed.",
        });
        setToUnlink(null);
      },
      onError: (err) => {
        toast({
          title: "Could not unlink the ticket",
          description: err.message,
          variant: "destructive",
        });
        setToUnlink(null);
      },
    });
  };

  const handleLink = (e: React.FormEvent) => {
    e.preventDefault();
    const issue = linkInput.trim();
    if (!issue) return;
    link(issue, {
      onSuccess: (ticket) => {
        toast({
          title: `${ticket.issue_key} linked`,
          description: ticket.summary,
        });
        setLinkInput("");
      },
      onError: (err) =>
        toast({
          title: "Could not link the ticket",
          description: err.message,
          variant: "destructive",
        }),
    });
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Jira Tickets</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tickets yet.</p>
        ) : (
          <ul className="space-y-1.5" aria-label="Jira tickets">
            {tickets.map((t) => (
              <li key={t.issue_key} className="flex items-stretch gap-1">
                <a
                  href={t.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted"
                >
                  <span className="min-w-0">
                    <span className="font-semibold">{t.issue_key}</span>
                    {t.is_primary && (
                      <span className="ml-2 rounded bg-blue-100 dark:bg-blue-950 px-1.5 text-xs text-blue-700 dark:text-blue-300">
                        Primary
                      </span>
                    )}
                    {t.origin === "linked" && (
                      <span className="ml-2 rounded bg-muted px-1.5 text-xs text-muted-foreground">
                        Linked
                      </span>
                    )}
                    <span className="block truncate text-xs text-muted-foreground">
                      {t.summary}
                    </span>
                  </span>
                  <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
                </a>
                {!t.is_primary && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto shrink-0 px-2 text-muted-foreground hover:text-destructive"
                    onClick={() => setToUnlink(t.issue_key)}
                    aria-label={`Unlink ${t.issue_key}`}
                    title={`Unlink ${t.issue_key}`}
                  >
                    <Unlink className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {data?.can_create && (
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => setOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Raise Jira Ticket
          </Button>
        )}

        {data?.can_link && (
          <form onSubmit={handleLink} className="flex gap-2">
            <Input
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              placeholder="Paste a Jira ticket key or link"
              aria-label="Existing Jira ticket"
              className="h-9 text-sm"
            />
            <Button
              type="submit"
              variant="outline"
              size="sm"
              className="h-9 gap-1 shrink-0"
              disabled={!linkInput.trim() || isLinking}
            >
              {isLinking ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4" />
              )}
              Link
            </Button>
          </form>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Raise a Jira ticket</DialogTitle>
            <DialogDescription>
              Creates a Bug now, assigned to you and linked to this request.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="ticket-project">Project</Label>
              <Select value={project} onValueChange={setProject}>
                <SelectTrigger id="ticket-project">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.key} — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ticket-summary">Summary</Label>
              <Input
                id="ticket-summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                maxLength={255}
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={copyAttachments}
                onChange={(e) => setCopyAttachments(e.target.checked)}
                className="rounded border border-muted-foreground"
              />
              Copy this request's attachments to the ticket
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!project || !summary.trim() || isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!toUnlink}
        onOpenChange={(o) => !o && !isUnlinking && setToUnlink(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink {toUnlink}?</AlertDialogTitle>
            <AlertDialogDescription>
              It will no longer be listed on this request, and the Jira links
              Relay made to it are removed. The Jira ticket itself isn't deleted
              or changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnlinking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleUnlink();
              }}
              disabled={isUnlinking}
            >
              {isUnlinking ? "Unlinking…" : "Unlink"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
